import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Card,
  Tab,
  Nav,
  Table,
  Button,
  Badge,
  Modal,
  Form,
  Row,
  Col,
  Alert,
  Spinner
} from 'react-bootstrap';
import {
  bookingService,
  courseService,
  locationService,
  userService
} from '../../services/api';

const DAYS_OF_WEEK = [
  { value: 1, label: 'Lunedì' },
  { value: 2, label: 'Martedì' },
  { value: 3, label: 'Mercoledì' },
  { value: 4, label: 'Giovedì' },
  { value: 5, label: 'Venerdì' },
  { value: 6, label: 'Sabato' },
  { value: 0, label: 'Domenica' }
];

const getDayName = (day) => {
  const found = DAYS_OF_WEEK.find((d) => d.value === day);
  return found ? found.label : `Giorno ${day}`;
};

const BookingSettings = () => {
  const [activeTab, setActiveTab] = useState('templates');

  // Common options
  const [courses, setCourses] = useState([]);
  const [locations, setLocations] = useState([]);
  const [coaches, setCoaches] = useState([]);

  // Templates state
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({
    dayOfWeek: 1,
    startTime: '09:00',
    endTime: '13:00',
    slotDurationMinutes: 60,
    maxParticipantsPerSlot: '',
    courseId: '',
    coachId: '',
    locationId: '',
    title: '',
    isActive: true
  });

  // Overrides state
  const [overrides, setOverrides] = useState([]);
  const [loadingOverrides, setLoadingOverrides] = useState(false);
  const [showOverrideModal, setShowOverrideModal] = useState(false);
  const [overrideForm, setOverrideForm] = useState({
    date: new Date().toISOString().split('T')[0],
    startTime: '09:00',
    endTime: '13:00',
    slotDurationMinutes: 60,
    maxParticipantsPerSlot: '',
    courseId: '',
    coachId: '',
    locationId: '',
    title: '',
    isAvailable: true
  });

  // Bookings list state
  const [bookings, setBookings] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(false);
  const [bookingFilterStatus, setBookingFilterStatus] = useState('');
  const [bookingFilterCourse, setBookingFilterCourse] = useState('');
  const [bookingFilterDate, setBookingFilterDate] = useState('');

  // Settings state
  const [settings, setSettings] = useState({
    minAdvanceHours: 1,
    maxAdvanceDays: 30,
    cancelMinHours: 4
  });
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);

  // Status banners
  const [feedback, setFeedback] = useState({ type: '', message: '' });

  const showNotification = (type, message) => {
    setFeedback({ type, message });
    setTimeout(() => setFeedback({ type: '', message: '' }), 5000);
  };

  // Load common options
  const loadOptions = useCallback(async () => {
    try {
      const [coursesRes, locsRes, coachesRes] = await Promise.all([
        typeof courseService.getCoursesSummary === "function" ? courseService.getCoursesSummary("") : courseService.getCourses({ limit: 100 }),
        locationService.getAll(false),
        userService.getUsers({ role: 'tech', limit: 100 })
      ]);

      if (coursesRes?.success) {
        const rawList = Array.isArray(coursesRes.data)
          ? coursesRes.data
          : coursesRes.data?.items || coursesRes.data?.courses || [];
        const cList = rawList.map((item) => (item?.course ? { ...item.course } : item));
        setCourses(cList);
      }
      if (locsRes.success) {
        const lList = Array.isArray(locsRes.data)
          ? locsRes.data
          : locsRes.data?.items || [];
        setLocations(lList);
      }
      if (coachesRes.success) {
        const uList = Array.isArray(coachesRes.data)
          ? coachesRes.data
          : coachesRes.data?.items || coachesRes.data?.users || [];
        setCoaches(uList);
      }
    } catch (err) {
      console.error('Error loading options:', err);
    }
  }, []);

  // Load Templates
  const loadTemplates = useCallback(async () => {
    setLoadingTemplates(true);
    const res = await bookingService.getTemplates();
    setLoadingTemplates(false);
    if (res.success && res.data) {
      setTemplates(res.data);
    } else {
      showNotification('danger', res.message || 'Errore nel caricamento dei template');
    }
  }, []);

  // Load Overrides
  const loadOverrides = useCallback(async () => {
    setLoadingOverrides(true);
    const res = await bookingService.getOverrides();
    setLoadingOverrides(false);
    if (res.success && res.data) {
      setOverrides(res.data);
    } else {
      showNotification('danger', res.message || 'Errore nel caricamento delle eccezioni');
    }
  }, []);

  // Load Bookings
  const loadBookings = useCallback(async () => {
    setLoadingBookings(true);
    const params = {
      status: bookingFilterStatus || undefined,
      courseId: bookingFilterCourse || undefined,
      startDate: bookingFilterDate || undefined
    };
    const res = await bookingService.getBookings(params);
    setLoadingBookings(false);
    if (res.success && res.data) {
      setBookings(res.data);
    } else {
      showNotification('danger', res.message || 'Errore nel caricamento delle prenotazioni');
    }
  }, [bookingFilterStatus, bookingFilterCourse, bookingFilterDate]);

  // Load Settings
  const loadSettings = useCallback(async () => {
    setLoadingSettings(true);
    const res = await bookingService.getSettings();
    setLoadingSettings(false);
    if (res.success && res.data) {
      setSettings(res.data);
    }
  }, []);

  useEffect(() => {
    loadOptions();
    loadTemplates();
  }, [loadOptions, loadTemplates]);

  useEffect(() => {
    if (activeTab === 'templates') loadTemplates();
    if (activeTab === 'overrides') loadOverrides();
    if (activeTab === 'bookings') loadBookings();
    if (activeTab === 'settings') loadSettings();
  }, [activeTab, loadTemplates, loadOverrides, loadBookings, loadSettings]);

  // Open Template Modal
  const handleOpenTemplateModal = (template = null) => {
    if (template) {
      setEditingTemplate(template);
      setTemplateForm({
        dayOfWeek: template.dayOfWeek,
        startTime: template.startTime,
        endTime: template.endTime,
        slotDurationMinutes: template.slotDurationMinutes,
        maxParticipantsPerSlot: template.maxParticipantsPerSlot ?? '',
        courseId: template.courseId || '',
        coachId: template.coachId,
        locationId: template.locationId,
        title: template.title || '',
        isActive: template.isActive
      });
    } else {
      setEditingTemplate(null);
      setTemplateForm({
        dayOfWeek: 1,
        startTime: '09:00',
        endTime: '13:00',
        slotDurationMinutes: 60,
        maxParticipantsPerSlot: '',
        courseId: '',
        coachId: coaches[0]?.id || '',
        locationId: locations[0]?.id || '',
        title: '',
        isActive: true
      });
    }
    setShowTemplateModal(true);
  };

  const handleSaveTemplate = async (e) => {
    e.preventDefault();
    if (!templateForm.coachId || !templateForm.locationId) {
      showNotification('warning', 'Seleziona un istruttore e un luogo obbligatoriamente.');
      return;
    }

    const payload = {
      ...templateForm,
      courseId: templateForm.courseId ? templateForm.courseId : null,
      maxParticipantsPerSlot: templateForm.maxParticipantsPerSlot
        ? parseInt(templateForm.maxParticipantsPerSlot, 10)
        : null,
      slotDurationMinutes: parseInt(templateForm.slotDurationMinutes, 10) || 60
    };

    let res;
    if (editingTemplate) {
      res = await bookingService.updateTemplate(editingTemplate.id, payload);
    } else {
      res = await bookingService.createTemplate(payload);
    }

    if (res.success) {
      showNotification('success', editingTemplate ? 'Template aggiornato con successo' : 'Template creato con successo');
      setShowTemplateModal(false);
      loadTemplates();
    } else {
      showNotification('danger', res.message || 'Errore durante il salvataggio del template');
    }
  };

  const handleDeleteTemplate = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo template di slot?')) return;
    const res = await bookingService.deleteTemplate(id);
    if (res.success) {
      showNotification('success', 'Template eliminato con successo');
      loadTemplates();
    } else {
      showNotification('danger', res.message || 'Impossibile eliminare il template');
    }
  };

  // Open Override Modal
  const handleOpenOverrideModal = () => {
    setOverrideForm({
      date: new Date().toISOString().split('T')[0],
      startTime: '09:00',
      endTime: '13:00',
      slotDurationMinutes: 60,
      maxParticipantsPerSlot: '',
      courseId: '',
      coachId: coaches[0]?.id || '',
      locationId: locations[0]?.id || '',
      title: '',
      isAvailable: true
    });
    setShowOverrideModal(true);
  };

  const handleSaveOverride = async (e) => {
    e.preventDefault();
    if (!overrideForm.coachId || !overrideForm.locationId) {
      showNotification('warning', 'Seleziona un istruttore e un luogo.');
      return;
    }

    const payload = {
      ...overrideForm,
      courseId: overrideForm.courseId ? overrideForm.courseId : null,
      maxParticipantsPerSlot: overrideForm.maxParticipantsPerSlot
        ? parseInt(overrideForm.maxParticipantsPerSlot, 10)
        : null,
      slotDurationMinutes: parseInt(overrideForm.slotDurationMinutes, 10) || 60
    };

    const res = await bookingService.createOverride(payload);
    if (res.success) {
      showNotification('success', 'Eccezione salvata con successo');
      setShowOverrideModal(false);
      loadOverrides();
    } else {
      showNotification('danger', res.message || 'Errore nel salvataggio dell\'eccezione');
    }
  };

  const handleDeleteOverride = async (id) => {
    if (!window.confirm('Eliminare questa eccezione di data/orario?')) return;
    const res = await bookingService.deleteOverride(id);
    if (res.success) {
      showNotification('success', 'Eccezione eliminata con successo');
      loadOverrides();
    } else {
      showNotification('danger', res.message || 'Errore durante l\'eliminazione');
    }
  };

  // Admin Cancel Booking
  const handleAdminCancelBooking = async (id) => {
    if (!window.confirm('Confermi la cancellazione della prenotazione dell\'atleta?')) return;
    const res = await bookingService.adminCancelBooking(id);
    if (res.success) {
      showNotification('success', 'Prenotazione cancellata');
      loadBookings();
    } else {
      showNotification('danger', res.message || 'Errore durante la cancellazione');
    }
  };

  // Save Settings
  const handleSaveSettings = async (e) => {
    e.preventDefault();
    setSavingSettings(true);
    const res = await bookingService.updateSettings({
      minAdvanceHours: parseInt(settings.minAdvanceHours, 10) || 1,
      maxAdvanceDays: parseInt(settings.maxAdvanceDays, 10) || 30,
      cancelMinHours: parseInt(settings.cancelMinHours, 10) || 4
    });
    setSavingSettings(false);
    if (res.success) {
      showNotification('success', 'Parametri salvati con successo');
    } else {
      showNotification('danger', res.message || 'Errore nel salvataggio dei parametri');
    }
  };

  return (
    <Container fluid className="py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="fw-bold mb-1">
            <i className="fas fa-calendar-alt text-primary me-2"></i>
            Gestione Prenotazioni & Slot
          </h2>
          <p className="text-muted mb-0">
            Configura le finestre di disponibilità settimanali, le eccezioni di data e consulta le prenotazioni degli atleti.
          </p>
        </div>
      </div>

      {feedback.message && (
        <Alert variant={feedback.type} dismissible onClose={() => setFeedback({ type: '', message: '' })}>
          {feedback.message}
        </Alert>
      )}

      <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
        <Card className="shadow-sm border-0 mb-4">
          <Card.Header className="bg-white border-bottom-0 pb-0">
            <Nav variant="tabs">
              <Nav.Item>
                <Nav.Link eventKey="templates" className="fw-semibold">
                  <i className="fas fa-redo me-2"></i>
                  Template Settimanali ({templates.length})
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="overrides" className="fw-semibold">
                  <i className="fas fa-calendar-times me-2"></i>
                  Eccezioni & Date Specifiche ({overrides.length})
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="bookings" className="fw-semibold">
                  <i className="fas fa-list-check me-2"></i>
                  Prenotazioni Atleti ({bookings.length})
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="settings" className="fw-semibold">
                  <i className="fas fa-sliders-h me-2"></i>
                  Regole & Anticipo
                </Nav.Link>
              </Nav.Item>
            </Nav>
          </Card.Header>

          <Card.Body className="p-4">
            <Tab.Content>
              {/* TAB 1: TEMPLATE SETTIMANALI */}
              <Tab.Pane eventKey="templates">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="fw-bold mb-0">Fasce Orarie Ricorrenti</h5>
                  <Button variant="primary" onClick={() => handleOpenTemplateModal()}>
                    <i className="fas fa-plus me-1"></i> Nuovo Template
                  </Button>
                </div>

                {loadingTemplates ? (
                  <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-2 text-muted">Caricamento template in corso...</p>
                  </div>
                ) : templates.length === 0 ? (
                  <div className="text-center py-5 border rounded bg-light">
                    <i className="fas fa-calendar-plus fa-3x text-muted mb-3"></i>
                    <h6>Nessun template di slot settimanale configurato</h6>
                    <p className="text-muted small">
                      Crea un template per impostare quando gli atleti possono prenotare lezioni o fasce orarie.
                    </p>
                    <Button variant="primary" size="sm" onClick={() => handleOpenTemplateModal()}>
                      Crea il primo template
                    </Button>
                  </div>
                ) : (
                  <Table responsive hover className="align-middle">
                    <thead className="table-light">
                      <tr>
                        <th>Giorno</th>
                        <th>Orario Finestra</th>
                        <th>Durata Slot</th>
                        <th>Corso</th>
                        <th>Istruttore</th>
                        <th>Luogo</th>
                        <th>Max Posti</th>
                        <th>Stato</th>
                        <th className="text-end">Azioni</th>
                      </tr>
                    </thead>
                    <tbody>
                      {templates.map((t) => (
                        <tr key={t.id}>
                          <td>
                            <strong>{getDayName(t.dayOfWeek)}</strong>
                            {t.title && <div className="text-muted small">{t.title}</div>}
                          </td>
                          <td>
                            <Badge bg="secondary" className="px-2 py-1">
                              {t.startTime} - {t.endTime}
                            </Badge>
                          </td>
                          <td>{t.slotDurationMinutes} min</td>
                          <td>
                            {t.courseName ? (
                              <Badge bg="info" className="text-dark">
                                {t.courseName}
                              </Badge>
                            ) : (
                              <span className="text-muted fst-italic">Qualsiasi corso (libero)</span>
                            )}
                          </td>
                          <td>{t.coachName}</td>
                          <td>{t.locationName}</td>
                          <td>
                            {t.maxParticipantsPerSlot ? (
                              <span className="fw-bold">{t.maxParticipantsPerSlot}</span>
                            ) : (
                              <span className="text-muted">Illimitati</span>
                            )}
                          </td>
                          <td>
                            <Badge bg={t.isActive ? 'success' : 'danger'}>
                              {t.isActive ? 'Attivo' : 'Disattivato'}
                            </Badge>
                          </td>
                          <td className="text-end">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              className="me-2"
                              onClick={() => handleOpenTemplateModal(t)}
                            >
                              <i className="fas fa-edit"></i>
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleDeleteTemplate(t.id)}
                            >
                              <i className="fas fa-trash"></i>
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Tab.Pane>

              {/* TAB 2: OVERRIDES / ECCEZIONI */}
              <Tab.Pane eventKey="overrides">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div>
                    <h5 className="fw-bold mb-0">Date Specifiche & Chiusure</h5>
                    <small className="text-muted">
                      Aggiungi aperture straordinarie o blocca giornate/orari (es. festività, chiusure).
                    </small>
                  </div>
                  <Button variant="primary" onClick={handleOpenOverrideModal}>
                    <i className="fas fa-plus me-1"></i> Nuova Eccezione
                  </Button>
                </div>

                {loadingOverrides ? (
                  <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                  </div>
                ) : overrides.length === 0 ? (
                  <div className="text-center py-5 border rounded bg-light">
                    <i className="fas fa-calendar-check fa-3x text-muted mb-3"></i>
                    <h6>Nessuna eccezione configurata</h6>
                    <p className="text-muted small">
                      Usa le eccezioni per chiusure straordinarie o aperture speciali in date singole.
                    </p>
                  </div>
                ) : (
                  <Table responsive hover className="align-middle">
                    <thead className="table-light">
                      <tr>
                        <th>Data</th>
                        <th>Orario</th>
                        <th>Tipo</th>
                        <th>Corso</th>
                        <th>Istruttore & Luogo</th>
                        <th>Max Posti</th>
                        <th className="text-end">Azioni</th>
                      </tr>
                    </thead>
                    <tbody>
                      {overrides.map((o) => (
                        <tr key={o.id}>
                          <td>
                            <strong>{o.date}</strong>
                            {o.title && <div className="text-muted small">{o.title}</div>}
                          </td>
                          <td>
                            {o.startTime} - {o.endTime} ({o.slotDurationMinutes} min)
                          </td>
                          <td>
                            {o.isAvailable ? (
                              <Badge bg="success">Disponibilità Straordinaria</Badge>
                            ) : (
                              <Badge bg="danger">Blocco / Chiusura</Badge>
                            )}
                          </td>
                          <td>{o.courseName || <span className="text-muted">Qualsiasi</span>}</td>
                          <td>
                            {o.coachName} @ {o.locationName}
                          </td>
                          <td>{o.maxParticipantsPerSlot ?? 'Illimitati'}</td>
                          <td className="text-end">
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleDeleteOverride(o.id)}
                            >
                              <i className="fas fa-trash"></i>
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Tab.Pane>

              {/* TAB 3: REGISTRO PRENOTAZIONI */}
              <Tab.Pane eventKey="bookings">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="fw-bold mb-0">Prenotazioni Ricevute</h5>
                  <Button variant="outline-secondary" size="sm" onClick={loadBookings}>
                    <i className="fas fa-sync me-1"></i> Aggiorna
                  </Button>
                </div>

                {/* Filtri */}
                <Card className="bg-light border-0 mb-3 p-3">
                  <Row className="g-2">
                    <Col md={3}>
                      <Form.Label className="small fw-semibold">Filtra per Stato</Form.Label>
                      <Form.Select
                        size="sm"
                        value={bookingFilterStatus}
                        onChange={(e) => setBookingFilterStatus(e.target.value)}
                      >
                        <option value="">Tutti gli stati</option>
                        <option value="confirmed">Confermate</option>
                        <option value="cancelled">Cancellate</option>
                      </Form.Select>
                    </Col>
                    <Col md={4}>
                      <Form.Label className="small fw-semibold">Filtra per Corso</Form.Label>
                      <Form.Select
                        size="sm"
                        value={bookingFilterCourse}
                        onChange={(e) => setBookingFilterCourse(e.target.value)}
                      >
                        <option value="">Tutti i corsi</option>
                        {courses.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name} {c.academicYear ? `(${c.academicYear}/${c.academicYear + 1})` : ""}
                          </option>
                        ))}
                      </Form.Select>
                    </Col>
                    <Col md={3}>
                      <Form.Label className="small fw-semibold">A partire dal</Form.Label>
                      <Form.Control
                        type="date"
                        size="sm"
                        value={bookingFilterDate}
                        onChange={(e) => setBookingFilterDate(e.target.value)}
                      />
                    </Col>
                    <Col md={2} className="d-flex align-items-end">
                      <Button
                        variant="secondary"
                        size="sm"
                        className="w-100"
                        onClick={() => {
                          setBookingFilterStatus('');
                          setBookingFilterCourse('');
                          setBookingFilterDate('');
                        }}
                      >
                        Reset Filtri
                      </Button>
                    </Col>
                  </Row>
                </Card>

                {loadingBookings ? (
                  <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                  </div>
                ) : bookings.length === 0 ? (
                  <div className="text-center py-5 border rounded bg-light">
                    <i className="fas fa-inbox fa-3x text-muted mb-3"></i>
                    <h6>Nessuna prenotazione trovata con i filtri selezionati</h6>
                  </div>
                ) : (
                  <Table responsive hover className="align-middle">
                    <thead className="table-light">
                      <tr>
                        <th>Data & Ora Slot</th>
                        <th>Atleta</th>
                        <th>Corso</th>
                        <th>Istruttore & Luogo</th>
                        <th>Prenotato il</th>
                        <th>Stato</th>
                        <th className="text-end">Azioni</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bookings.map((b) => (
                        <tr key={b.id} className={b.status === 'cancelled' ? 'table-light text-muted' : ''}>
                          <td>
                            <strong>{b.date}</strong>
                            <div>
                              <Badge bg="secondary">
                                {b.startTime} - {b.endTime}
                              </Badge>
                            </div>
                          </td>
                          <td>
                            <strong>{b.userName}</strong>
                            {b.userEmail && <div className="small text-muted">{b.userEmail}</div>}
                          </td>
                          <td>
                            <Badge bg="primary">{b.courseName || 'Corso'}</Badge>
                          </td>
                          <td>
                            {b.coachName}
                            <div className="small text-muted">{b.locationName}</div>
                          </td>
                          <td className="small">
                            {new Date(b.bookedAt).toLocaleDateString('it-IT')}{' '}
                            {new Date(b.bookedAt).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                          </td>
                          <td>
                            <Badge bg={b.status === 'confirmed' ? 'success' : 'danger'}>
                              {b.status === 'confirmed' ? 'Confermata' : 'Cancellata'}
                            </Badge>
                          </td>
                          <td className="text-end">
                            {b.status === 'confirmed' && (
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => handleAdminCancelBooking(b.id)}
                              >
                                Cancella
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Tab.Pane>

              {/* TAB 4: REGOLE E ANTICIPO */}
              <Tab.Pane eventKey="settings">
                <Row>
                  <Col md={8} lg={6}>
                    <Card className="border p-4">
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5 className="fw-bold mb-0">Parametri Globali di Prenotazione</h5>
                        {loadingSettings && <Spinner animation="border" size="sm" />}
                      </div>
                      <Form onSubmit={handleSaveSettings}>
                        <Form.Group className="mb-3">
                          <Form.Label className="fw-semibold">
                            Anticipo minimo per prenotare (in ore)
                          </Form.Label>
                          <Form.Control
                            type="number"
                            min="0"
                            max="168"
                            value={settings.minAdvanceHours}
                            onChange={(e) =>
                              setSettings({ ...settings, minAdvanceHours: e.target.value })
                            }
                            required
                          />
                          <Form.Text className="text-muted">
                            Esempio: 2 ore significa che l&apos;atleta può prenotare fino a 2 ore prima dell&apos;inizio.
                          </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-3">
                          <Form.Label className="fw-semibold">
                            Finestra massima di prenotazione futura (in giorni)
                          </Form.Label>
                          <Form.Control
                            type="number"
                            min="1"
                            max="365"
                            value={settings.maxAdvanceDays}
                            onChange={(e) =>
                              setSettings({ ...settings, maxAdvanceDays: e.target.value })
                            }
                            required
                          />
                          <Form.Text className="text-muted">
                            Esempio: 30 giorni permette di visualizzare e prenotare slot fino a un mese in avanti.
                          </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-4">
                          <Form.Label className="fw-semibold">
                            Tempo limite per la cancellazione autonoma (in ore)
                          </Form.Label>
                          <Form.Control
                            type="number"
                            min="0"
                            max="72"
                            value={settings.cancelMinHours}
                            onChange={(e) =>
                              setSettings({ ...settings, cancelMinHours: e.target.value })
                            }
                            required
                          />
                          <Form.Text className="text-muted">
                            Esempio: 4 ore significa che l&apos;atleta può cancellare la prenotazione autonomamente fino a 4 ore prima dell&apos;inizio dello slot.
                          </Form.Text>
                        </Form.Group>

                        <Button variant="primary" type="submit" disabled={savingSettings}>
                          {savingSettings ? (
                            <>
                              <Spinner animation="border" size="sm" className="me-2" />
                              Salvataggio in corso...
                            </>
                          ) : (
                            <>
                              <i className="fas fa-save me-1"></i> Salva Parametri
                            </>
                          )}
                        </Button>
                      </Form>
                    </Card>
                  </Col>
                </Row>
              </Tab.Pane>
            </Tab.Content>
          </Card.Body>
        </Card>
      </Tab.Container>

      {/* MODALE CREA/MODIFICA TEMPLATE */}
      <Modal show={showTemplateModal} onHide={() => setShowTemplateModal(false)} size="lg">
        <Form onSubmit={handleSaveTemplate}>
          <Modal.Header closeButton>
            <Modal.Title className="fw-bold">
              {editingTemplate ? 'Modifica Template Slot' : 'Nuovo Template Slot Settimanale'}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold">Giorno della Settimana *</Form.Label>
                  <Form.Select
                    value={templateForm.dayOfWeek}
                    onChange={(e) =>
                      setTemplateForm({ ...templateForm, dayOfWeek: parseInt(e.target.value, 10) })
                    }
                    required
                  >
                    {DAYS_OF_WEEK.map((d) => (
                      <option key={d.value} value={d.value}>
                        {d.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold">Titolo o Note (opzionale)</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="es. Pilates Avanzato, Sala Pesi..."
                    value={templateForm.title}
                    onChange={(e) => setTemplateForm({ ...templateForm, title: e.target.value })}
                  />
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group>
                  <Form.Label className="fw-semibold">Ora Inizio Finestra *</Form.Label>
                  <Form.Control
                    type="time"
                    value={templateForm.startTime}
                    onChange={(e) => setTemplateForm({ ...templateForm, startTime: e.target.value })}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="fw-semibold">Ora Fine Finestra *</Form.Label>
                  <Form.Control
                    type="time"
                    value={templateForm.endTime}
                    onChange={(e) => setTemplateForm({ ...templateForm, endTime: e.target.value })}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="fw-semibold">Durata Singolo Slot (minuti) *</Form.Label>
                  <Form.Control
                    type="number"
                    min="10"
                    max="480"
                    step="5"
                    value={templateForm.slotDurationMinutes}
                    onChange={(e) =>
                      setTemplateForm({ ...templateForm, slotDurationMinutes: e.target.value })
                    }
                    required
                  />
                  <Form.Text className="text-muted small">
                    Se uguale alla finestra, crea 1 solo slot (lezione intera).
                  </Form.Text>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold">Istruttore (Coach) *</Form.Label>
                  <Form.Select
                    value={templateForm.coachId}
                    onChange={(e) => setTemplateForm({ ...templateForm, coachId: e.target.value })}
                    required
                  >
                    <option value="">Seleziona un istruttore...</option>
                    {coaches.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.firstName} {c.lastName}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold">Luogo (Location) *</Form.Label>
                  <Form.Select
                    value={templateForm.locationId}
                    onChange={(e) =>
                      setTemplateForm({ ...templateForm, locationId: e.target.value })
                    }
                    required
                  >
                    <option value="">Seleziona un luogo...</option>
                    {locations.map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold">Corso Specifico (opzionale)</Form.Label>
                  <Form.Select
                    value={templateForm.courseId}
                    onChange={(e) => setTemplateForm({ ...templateForm, courseId: e.target.value })}
                  >
                    <option value="">Nessuno (Slot generico / Lezione libera)</option>
                    {courses.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name} {c.academicYear ? `(${c.academicYear}/${c.academicYear + 1})` : ""}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted small">
                    Se selezionato, visibile solo agli iscritti a questo corso.
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold">Max Partecipanti per Slot</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    placeholder="Lascia vuoto per posti illimitati"
                    value={templateForm.maxParticipantsPerSlot}
                    onChange={(e) =>
                      setTemplateForm({ ...templateForm, maxParticipantsPerSlot: e.target.value })
                    }
                  />
                  <Form.Text className="text-muted small">
                    1 = Lezione one-to-one, 8 = Gruppo max 8 persone.
                  </Form.Text>
                </Form.Group>
              </Col>

              <Col md={12}>
                <Form.Check
                  type="switch"
                  id="template-active-switch"
                  label="Template attivo e disponibile per la prenotazione"
                  checked={templateForm.isActive}
                  onChange={(e) =>
                    setTemplateForm({ ...templateForm, isActive: e.target.checked })
                  }
                />
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowTemplateModal(false)}>
              Annulla
            </Button>
            <Button variant="primary" type="submit">
              {editingTemplate ? 'Aggiorna Template' : 'Crea Template'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* MODALE CREA OVERRIDE / ECCEZIONE */}
      <Modal show={showOverrideModal} onHide={() => setShowOverrideModal(false)} size="lg">
        <Form onSubmit={handleSaveOverride}>
          <Modal.Header closeButton>
            <Modal.Title className="fw-bold">Nuova Eccezione o Data Specifica</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold">Data Specifica *</Form.Label>
                  <Form.Control
                    type="date"
                    value={overrideForm.date}
                    onChange={(e) => setOverrideForm({ ...overrideForm, date: e.target.value })}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold">Tipo di Eccezione *</Form.Label>
                  <Form.Select
                    value={overrideForm.isAvailable ? 'true' : 'false'}
                    onChange={(e) =>
                      setOverrideForm({ ...overrideForm, isAvailable: e.target.value === 'true' })
                    }
                  >
                    <option value="true">Disponibilità straordinaria (Slot prenotabili)</option>
                    <option value="false">Blocco / Chiusura (Slot non prenotabili)</option>
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group>
                  <Form.Label className="fw-semibold">Ora Inizio *</Form.Label>
                  <Form.Control
                    type="time"
                    value={overrideForm.startTime}
                    onChange={(e) => setOverrideForm({ ...overrideForm, startTime: e.target.value })}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="fw-semibold">Ora Fine *</Form.Label>
                  <Form.Control
                    type="time"
                    value={overrideForm.endTime}
                    onChange={(e) => setOverrideForm({ ...overrideForm, endTime: e.target.value })}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="fw-semibold">Durata Slot (minuti)</Form.Label>
                  <Form.Control
                    type="number"
                    min="10"
                    max="480"
                    value={overrideForm.slotDurationMinutes}
                    onChange={(e) =>
                      setOverrideForm({ ...overrideForm, slotDurationMinutes: e.target.value })
                    }
                    disabled={!overrideForm.isAvailable}
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold">Istruttore *</Form.Label>
                  <Form.Select
                    value={overrideForm.coachId}
                    onChange={(e) => setOverrideForm({ ...overrideForm, coachId: e.target.value })}
                    required
                  >
                    <option value="">Seleziona...</option>
                    {coaches.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.firstName} {c.lastName}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold">Luogo *</Form.Label>
                  <Form.Select
                    value={overrideForm.locationId}
                    onChange={(e) => setOverrideForm({ ...overrideForm, locationId: e.target.value })}
                    required
                  >
                    <option value="">Seleziona...</option>
                    {locations.map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold">Corso Specifico (opzionale)</Form.Label>
                  <Form.Select
                    value={overrideForm.courseId}
                    onChange={(e) => setOverrideForm({ ...overrideForm, courseId: e.target.value })}
                    disabled={!overrideForm.isAvailable}
                  >
                    <option value="">Nessuno (Qualsiasi corso)</option>
                    {courses.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name} {c.academicYear ? `(${c.academicYear}/${c.academicYear + 1})` : ""}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold">Max Partecipanti per Slot</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    placeholder="Vuoto per illimitati"
                    value={overrideForm.maxParticipantsPerSlot}
                    onChange={(e) =>
                      setOverrideForm({ ...overrideForm, maxParticipantsPerSlot: e.target.value })
                    }
                    disabled={!overrideForm.isAvailable}
                  />
                </Form.Group>
              </Col>

              <Col md={12}>
                <Form.Group>
                  <Form.Label className="fw-semibold">Motivo o Note</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="es. Festività, Chiusura Natalizia, Sessione straordinaria..."
                    value={overrideForm.title}
                    onChange={(e) => setOverrideForm({ ...overrideForm, title: e.target.value })}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowOverrideModal(false)}>
              Annulla
            </Button>
            <Button variant="primary" type="submit">
              Salva Eccezione
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
};

export default BookingSettings;
